import { useState, useEffect } from "react";
import "./styles.css";

export default function App() {
  const rows = 20;
  const cols = 20;

  const [snake, setSnake] = useState([{ x: 10, y: 10 }]);
  const [direction, setDirection] = useState("RIGHT");
  const [food, setFood] = useState(generateFood());
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [paused, setPaused] = useState(false);

  // 📏 Responsive board size
  const maxBoardSize = Math.min(window.innerWidth, window.innerHeight) * 0.6;
  const cellSize = Math.floor(maxBoardSize / cols);

  function generateFood() {
    return {
      x: Math.floor(Math.random() * cols),
      y: Math.floor(Math.random() * rows),
    };
  }

  function resetGame() {
    setSnake([{ x: 10, y: 10 }]);
    setDirection("RIGHT");
    setFood(generateFood());
    setScore(0);
    setGameOver(false);
    setPaused(false);
  }

  // 🎯 Safe direction
  const changeDirection = (newDir) => {
    setDirection((prev) => {
      if (newDir === "UP" && prev !== "DOWN") return "UP";
      if (newDir === "DOWN" && prev !== "UP") return "DOWN";
      if (newDir === "LEFT" && prev !== "RIGHT") return "LEFT";
      if (newDir === "RIGHT" && prev !== "LEFT") return "RIGHT";
      return prev;
    });
  };

  // 🎮 Keyboard
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === " ") {
        setPaused((p) => !p);
        return;
      }
      if (e.key === "ArrowUp") changeDirection("UP");
      if (e.key === "ArrowDown") changeDirection("DOWN");
      if (e.key === "ArrowLeft") changeDirection("LEFT");
      if (e.key === "ArrowRight") changeDirection("RIGHT");
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // 🕒 Game Loop
  useEffect(() => {
    if (gameOver || paused) return;

    const interval = setInterval(() => {
      setSnake((prevSnake) => {
        const head = prevSnake[0];
        let newHead = { ...head };

        if (direction === "RIGHT") newHead.x += 1;
        if (direction === "LEFT") newHead.x -= 1;
        if (direction === "UP") newHead.y -= 1;
        if (direction === "DOWN") newHead.y += 1;

        // Wall collision
        if (
          newHead.x < 0 ||
          newHead.x >= cols ||
          newHead.y < 0 ||
          newHead.y >= rows
        ) {
          setGameOver(true);
          return prevSnake;
        }

        // Self collision
        if (
          prevSnake.some(
            (seg) => seg.x === newHead.x && seg.y === newHead.y
          )
        ) {
          setGameOver(true);
          return prevSnake;
        }

        // Eat food
        if (newHead.x === food.x && newHead.y === food.y) {
          setFood(generateFood());
          setScore((prev) => prev + 1);
          return [newHead, ...prevSnake];
        }

        return [newHead, ...prevSnake.slice(0, -1)];
      });
    }, 250);

    return () => clearInterval(interval);
  }, [direction, food, gameOver, paused]);

  return (
    <div className="container">
      <h1>🐍 Snake Game</h1>
      <h3>Score: {score}</h3>

      {paused && !gameOver && <h3>⏸️ Game Paused</h3>}
      {gameOver && <h2 className="game-over">❌ Game Over</h2>}

      <div
        className="board"
        style={{
          gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
          gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`
        }}
      >
        {Array.from({ length: rows * cols }).map((_, index) => {
          const x = index % cols;
          const y = Math.floor(index / cols);

          const isSnake = snake.some(
            (seg) => seg.x === x && seg.y === y
          );
          const isFood = food.x === x && food.y === y;

          return (
            <div
              key={index}
              className={`cell ${isSnake ? "snake" : isFood ? "food" : ""}`}
            />
          );
        })}
      </div>

      {/* Controls */}
      <div className="controls">
        <button onClick={() => changeDirection("UP")}>⬆️</button>
        <div>
          <button onClick={() => changeDirection("LEFT")}>⬅️</button>
          <button onClick={() => changeDirection("DOWN")}>⬇️</button>
          <button onClick={() => changeDirection("RIGHT")}>➡️</button>
        </div>
      </div>

      {/* Actions */}
      <div className="actions">
        <button
          className="pause-btn"
          onClick={() => setPaused((p) => !p)}
        >
          {paused ? "▶ Resume" : "⏸ Pause"}
        </button>

        <button className="restart-btn" onClick={resetGame}>
          🔄 Restart
        </button>
      </div>
    </div>
  );
}
